/**
 * Typed pub/sub bus for "the agent did something, update the content panel"
 * commands (web three-pane layout). Same module-level listener pattern as
 * `components/tour/tour-actions.ts`.
 *
 * Emitters: the live chat stream's tool-result handler and the approval
 * confirmation path (see `agent-panel-sync.ts`). Subscriber: the web shell,
 * which translates `data.invalidate` into React Query invalidations. On
 * native nothing subscribes, so emitting is a harmless no-op.
 */

export type PanelCommand =
  | {
      type: "data.invalidate";
      /** React Query key prefixes to invalidate (prefix match). */
      keys: readonly (readonly string[])[];
    }
  | { type: "panel.navigate"; href: string }
  | {
      type: "jobs.filters.apply";
      filters: {
        query: string;
        location: string[];
        workMode: string | null;
        jobType: string | null;
        source: string | null;
        applyMode: string | null;
        datePosted: string | null;
      };
    }
  | { type: "nova.refresh" }
  | { type: "messages.refresh" }
  | { type: "notifications.refresh" };

type PanelCommandListener = (command: PanelCommand) => void;

const listeners = new Set<PanelCommandListener>();

export function subscribePanelCommand(listener: PanelCommandListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitPanelCommand(command: PanelCommand) {
  listeners.forEach((listener) => listener(command));
}

export function emitPanelCommands(commands: readonly PanelCommand[]) {
  commands.forEach((command) => emitPanelCommand(command));
}
